import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {CancelSubscriptionPanelState, InitCancelSubscriptionPanelResponse} from '@Panels/cancel-subscription-panel/cancel-subscription-panel.types';
import {cancelSubscription, initCancelSubscriptionPanel, pauseSubscription} from '@Panels/cancel-subscription-panel/cancel-subscription-panel-thunk';
import {CancelSubscriptionPanelStep, CancelSurveyReasonIDs, PauseIntervals} from '@Libraries/cancel-subscription-library';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';

const initialState: CancelSubscriptionPanelState = {
  isReady: false,
  isWaitingForAJAXResponse: false,
  currentStep: CancelSubscriptionPanelStep.FEATURE_USAGE,
  cancelReason: CancelSurveyReasonIDs.NONE,
  cancelReasonComments: '',
  featureUsages: null,
  pauseInterval: PauseIntervals.ONE_MONTH,
  subscriptionExpiresOnTimestamp: 0,
  pauseEndTimestamp: 0,
  transitionDirection: TransitionDirection.FORWARD,
  isRenewalInvoicePending: false,
  showPricingIncreaseBanner: false,
};

export const cancelSubscriptionPanelSlice = createSlice({
  name: 'cancelSubscriptionPanel',
  initialState,
  reducers: {
    updateCancelReason: (state, {payload}: PayloadAction<CancelSurveyReasonIDs>) => {
      state.cancelReason = payload;
    },
    updateCancelReasonComments: (state, {payload}: PayloadAction<string>) => {
      state.cancelReasonComments = payload;
    },
    updateStep: (
      state,
      {
        payload,
      }: PayloadAction<{
        step: CancelSubscriptionPanelStep;
        direction: TransitionDirection;
      }>
    ) => {
      state.currentStep = payload.step;
      state.transitionDirection = payload.direction;
    },
    updatePauseInterval: (state, {payload}: PayloadAction<PauseIntervals>) => {
      state.pauseInterval = payload;
    },
    updateIsWaitingForAJAXResponse: (state, {payload}: PayloadAction<boolean>) => {
      state.isWaitingForAJAXResponse = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initCancelSubscriptionPanel.pending, (state) => {
      state.isReady = false;
    });
    builder.addCase(initCancelSubscriptionPanel.fulfilled, (state, {payload}: PayloadAction<InitCancelSubscriptionPanelResponse>) => {
      state.isReady = true;
      state.featureUsages = payload.featureUsages;
      state.subscriptionExpiresOnTimestamp = payload.subscriptionExpiresOnTimestamp;
      state.isRenewalInvoicePending = payload.isRenewalInvoicePending;
      state.showPricingIncreaseBanner = payload.showPricingIncreaseBanner;
      state.currentStep = CancelSubscriptionPanelStep.FEATURE_USAGE;
    });

    builder.addCase(cancelSubscription.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(cancelSubscription.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = false;
    });

    builder.addCase(pauseSubscription.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(pauseSubscription.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
  },
});

export const {updateCancelReason, updateCancelReasonComments, updateStep, updatePauseInterval, updateIsWaitingForAJAXResponse} = cancelSubscriptionPanelSlice.actions;
export const cancelSubscriptionPanelReducer = cancelSubscriptionPanelSlice.reducer;
